import { CarIcon, MapPinIcon, TimerIcon } from '@phosphor-icons/react';
import { monthShort, weekdayShort } from '@voll-intelligence/dates';
import { useImageFallback } from '@voll-intelligence/ui-kit/hooks/use-image-fallback';
import { useCheckoutEnv } from '../../drawer/env_context.js';
import type { RentalItemSource } from '../../model/checkout_source.js';
import './item_detail.css';

type RentalItemDetail = RentalItemSource['detail'];

export interface RentalItemDetailProps {
  detail: RentalItemDetail;
  price: { amount: number; currency: string };
}

export function formatCompactDateTime(iso: string, locale: string): string {
  const time = formatTimePart(iso, locale);
  return [formatDatePart(iso, locale), time].filter(Boolean).join(', ');
}

function compactDateLabel(detail: RentalItemDetail, locale: string): string | undefined {
  if (detail.pickupAt && detail.dropoffAt) {
    return `${formatCompactDateTime(detail.pickupAt, locale)} → ${formatCompactDateTime(
      detail.dropoffAt,
      locale,
    )}`;
  }
  if (detail.pickupAt) return formatCompactDateTime(detail.pickupAt, locale);
  if (detail.dropoffAt) return formatCompactDateTime(detail.dropoffAt, locale);
  return undefined;
}

/** Compact variant — item summary inside the unified checkout card. This is
 *  intentionally NOT the rental offer-row (`VehicleRow`): checkout preview uses
 *  the same visual pattern as the flight checkout summary card. */
export function RentalItemDetailCompact({ detail, price }: Readonly<RentalItemDetailProps>) {
  const { copy, locale } = useCheckoutEnv();
  const location = detail.pickUpAgency || detail.pickUpAddress;
  const days = detail.days ? copy.price.dailies(detail.days) : undefined;
  const date = compactDateLabel(detail, locale);
  const vehicleLabel = detail.vehicleName ?? detail.rentalCompany;
  const thumb = useImageFallback(detail.thumbUrl);
  return (
    <div className="pcc-rental">
      <div className="pcc-rental__main">
        {date && (
          <p className="pcc-rental__date" aria-label={copy.rental.dateAria}>
            {date}
          </p>
        )}
        <div className="pcc-rental__detail">
          <span className="pcc-rental__thumb" aria-hidden="true">
            {thumb.src ? (
              <img src={thumb.src} alt="" onError={thumb.onError} />
            ) : (
              <CarIcon size={34} aria-hidden />
            )}
          </span>

          <div className="pcc-rental__identity">
            <p className="pcc-rental__category">{detail.category}</p>
            {/* O `title` guarda o valor inteiro para o caso raro em que nem duas
                linhas bastam (o CSS corta na segunda). */}
            {vehicleLabel && (
              <p className="pcc-rental__vehicle-name" title={vehicleLabel}>
                {vehicleLabel}
              </p>
            )}
          </div>

          <div className="pcc-rental__meta">
            {days && (
              <div className="pcc-rental__meta-item">
                <TimerIcon size={14} aria-hidden />
                <p className="pcc-rental__days" aria-label={copy.rental.daysAria}>
                  {days}
                </p>
              </div>
            )}
            {location && (
              <div className="pcc-rental__meta-item">
                <MapPinIcon size={14} aria-hidden />
                <p className="pcc-rental__location" aria-label={copy.rental.pickupAria}>
                  {location}
                </p>
              </div>
            )}
            <span className="pcc-rental__sr-price">
              {price.amount} {price.currency}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** "Seg 14 set": weekday and month names come from the dates tables. */
function formatDatePart(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${weekdayShort(d, locale)} ${String(d.getDate())} ${monthShort(d, locale)}`;
}

function formatTimePart(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(d);
}
